import type { AxiosRequestConfig } from 'axios'
import axios from 'axios'

import type { CmsPageAdmin, CmsPageType } from '@beaded/models'

import { API_BASE_URI } from 'constants/api'

import * as apiUtils from '.'

/** GET /api/v1/cms/:pageType (public) */
export const getPublic = (
  pageType: CmsPageType,
  options: AxiosRequestConfig = {},
): Promise<{ content?: unknown; items?: unknown[] }> =>
  apiUtils.GET(`/cms/${pageType}`, options)

/** GET /api/v1/cms/:pageType/:slug (public) */
export const getPublicItem = <T extends CmsPageType>(
  pageType: T,
  slug: string,
  options: AxiosRequestConfig = {},
): Promise<{
  id: string
  slug: string
  title: string
  content: Extract<CmsPageAdmin, { pageType: T }>['content']
  publishedAt?: string
}> => apiUtils.GET(`/cms/${pageType}/${slug}`, options)

/** GET /api/v1/admin/cms/types */
export const getTypes = (
  options: AxiosRequestConfig = {},
): Promise<{ key: string; label: string; kind: string }[]> =>
  apiUtils
    .GET(`/admin/cms/types`, options)
    .then(
      (data: { types: { key: string; label: string; kind: string }[] }) =>
        data.types,
    )

/** GET /api/v1/admin/cms/:pageType */
export const getAdmin = (
  pageType: CmsPageType,
  options: AxiosRequestConfig = {},
): Promise<{
  item?: CmsPageAdmin | null
  items?: CmsPageAdmin[]
  kind: 'singleton' | 'collection'
}> => apiUtils.GET(`/admin/cms/${pageType}`, options)

/** POST /api/v1/admin/cms/:pageType */
export const create = (
  pageType: CmsPageType,
  content: Record<string, unknown>,
  title?: string,
): Promise<{ success: boolean; item: CmsPageAdmin; publishWarning?: string }> =>
  apiUtils.POST(`/admin/cms/${pageType}`, { pageType, content, title })

/** PUT /api/v1/admin/cms/:pageType/:idOrSlug */
export const update = (
  pageType: CmsPageType,
  idOrSlug: string,
  data: {
    content: Record<string, unknown>
    title?: string
    published?: boolean
  },
): Promise<{ success: boolean; item: CmsPageAdmin; publishWarning?: string }> =>
  apiUtils.PUT(`/admin/cms/${pageType}/${idOrSlug}`, {
    pageType,
    idOrSlug,
    ...data,
  })

/** DELETE /api/v1/admin/cms/:pageType/:idOrSlug */
export const remove = (
  pageType: CmsPageType,
  idOrSlug: string,
): Promise<{ success: boolean }> =>
  apiUtils.DELETE(`/admin/cms/${pageType}/${idOrSlug}`)

/** GET /api/v1/admin/cms/:pageType/:id/history */
export const getHistory = (
  pageType: CmsPageType,
  id: string,
  options: AxiosRequestConfig = {},
): Promise<unknown[]> =>
  apiUtils
    .GET(`/admin/cms/${pageType}/${id}/history`, options)
    .then((data: { history: unknown[] }) => data.history)

/** POST /api/v1/admin/cms/images */
export const uploadImage = (
  file: File,
): Promise<{ url: string; warning?: string }> => {
  const formData = new FormData()
  formData.append('file', file, file.name)
  const config: AxiosRequestConfig = {
    headers: { 'Content-Type': 'multipart/form-data' },
    withCredentials: true,
  }
  return axios
    .post(`${API_BASE_URI()}/admin/cms/images`, formData, config)
    .then((res) => res.data)
}

export const seedLanding = () => apiUtils.POST(`/admin/cms/landing/seed`, {})
